import type { DayResult } from './types'
import { todayUtc } from './day'

const KEY = 'wayoff.v1'

export interface LocalState {
  results: Record<string, DayResult>
  badges: Record<string, string> // badge id -> ISO date earned
}

export function load(): LocalState {
  try { return { results: {}, badges: {}, ...JSON.parse(localStorage.getItem(KEY) || '{}') } }
  catch { return { results: {}, badges: {} } }
}
export function save(s: LocalState) { localStorage.setItem(KEY, JSON.stringify(s)) }

export function getResult(date: string): DayResult | undefined { return load().results[date] }

export function saveResult(r: DayResult) {
  const s = load()
  s.results[r.date] = r
  awardBadges(s, r)
  save(s)
}

const shift = (date: string, days: number) => new Date(Date.parse(date + 'T00:00:00Z') + days * 86400000).toISOString().slice(0, 10)

/**
 * Consecutive non-replay days ending today or yesterday.
 * With `freeze` (Plus): one missed day per calendar month is forgiven, and we report which.
 */
export function streakInfo(s: LocalState = load(), freeze = false): { current: number; frozen: string[] } {
  let d = todayUtc()
  const frozen: string[] = []
  const usedMonth = new Set<string>()
  let n = 0
  const played = (x: string) => s.results[x] && !s.results[x].replay
  if (!played(d)) d = shift(d, -1)
  while (true) {
    if (played(d)) { n++; d = shift(d, -1); continue }
    const m = d.slice(0, 7)
    // freeze bridges a single missed day only if the streak continues on the far side
    if (freeze && n > 0 && !usedMonth.has(m) && played(shift(d, -1))) {
      usedMonth.add(m); frozen.push(d); d = shift(d, -1); continue
    }
    break
  }
  return { current: n, frozen }
}

export function dailyStreak(s?: LocalState, freeze = false): number { return streakInfo(s ?? load(), freeze).current }

/** Current run of consecutive questions within 1.5×, across the most recent days. */
export function sharpStreak(s = load()): number {
  const days = Object.values(s.results).filter((r) => !r.replay).sort((a, b) => b.date.localeCompare(a.date))
  let n = 0
  for (const day of days) for (const r of [...day.results].reverse()) { if (r.points >= 4) n++; else return n }
  return n
}

export const BADGES: Record<string, { name: string; emoji: string; desc: string }> = {
  first_play: { name: 'Way Off', emoji: '🚀', desc: 'Played your first day' },
  first_bullseye: { name: 'First bullseye', emoji: '🎯', desc: 'Within 1.5× of the truth' },
  saw_it_coming: { name: 'Saw it coming', emoji: '🔮', desc: 'Bullseye on the hero question' },
  magnitude_master: { name: 'Magnitude Master', emoji: '🧠', desc: 'No 💀 in a whole day' },
  perfect: { name: 'Forty-five hundred', emoji: '👑', desc: '4,500+ in a day' },
  skull_day: { name: 'Humbled', emoji: '💀', desc: 'Three or more 💀 in one day' },
  week_streak: { name: 'Seven days', emoji: '🔥', desc: '7-day play streak' },
}

function awardBadges(s: LocalState, r: DayResult) {
  const now = new Date().toISOString()
  const give = (id: string) => { if (!s.badges[id]) s.badges[id] = now }
  give('first_play')
  if (r.results.some((x) => x.points >= 4)) give('first_bullseye')
  const hero = r.results[r.results.length - 1]
  if (hero != null && hero.points >= 4) give('saw_it_coming')
  if (r.results.every((x) => x.points > 0)) give('magnitude_master')
  if (r.score >= 4500) give('perfect')
  if (r.results.filter((x) => x.points === 0).length >= 3) give('skull_day')
  if (dailyStreak(s) >= 7) give('week_streak')
}

export interface Wrapped {
  month: string; days: number; total: number; max: number; sharp: number; avgOrdersOff: number; best?: DayResult; emoji: string
}

export function monthlyWrapped(month = todayUtc().slice(0, 7), s = load()): Wrapped {
  const days = Object.values(s.results).filter((r) => r.date.startsWith(month) && !r.replay)
  const all = days.flatMap((d) => d.results)
  const sharp = all.filter((r) => r.points >= 4).length
  const avg = all.length ? all.reduce((a, r) => a + Math.log10(r.ratio), 0) / all.length : 0
  const best = [...days].sort((a, b) => b.score - a.score)[0]
  const counts = [0, 0, 0, 0, 0, 0]; for (const r of all) counts[r.points]++
  const emoji = ['🎯', '👍', '🙂', '😐', '😬', '💀'].map((e, i) => `${e}${counts[5 - i]}`).join(' ')
  return { month, days: days.length, total: days.reduce((a, d) => a + d.score, 0), max: days.length * 5000, sharp, avgOrdersOff: avg, best, emoji }
}
